import { useCallback, useEffect, useRef, useState } from 'react';

/* ---------------------------------------------------------------------------
   הקלטה קולית בדפדפן (MediaRecorder).

   Chrome/Firefox מקליטים webm/opus, Safari/iOS — mp4/aac. בוחרים את הראשון
   שהדפדפן תומך בו; השרת מזהה את הסוג מהתוכן ממילא. ההקלטה נשלחת לתמלול
   ונזרקת — היא לא נשמרת בשום מקום.
   --------------------------------------------------------------------------- */

const MIME_PREFERENCE = [
  'audio/webm;codecs=opus',
  'audio/mp4',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/aac',
];

/** שתי דקות — הודעה קולית, לא הכתבה. גם תקרת 10MB של השרת רחוקה מזה. */
export const MAX_RECORDING_SECONDS = 120;

export type RecorderState = 'idle' | 'requesting' | 'recording' | 'unsupported' | 'denied';

export function isRecordingSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.MediaRecorder !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function'
  );
}

function pickMime(): string | undefined {
  const Recorder = window.MediaRecorder;
  if (!('isTypeSupported' in Recorder)) return undefined;
  return MIME_PREFERENCE.find((m) => Recorder.isTypeSupported(m));
}

export function useVoiceRecorder(onRecorded: (audio: Blob) => void) {
  const [state, setState] = useState<RecorderState>(() =>
    isRecordingSupported() ? 'idle' : 'unsupported',
  );
  const [seconds, setSeconds] = useState(0);
  const recorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const chunks = useRef<Blob[]>([]);
  const cancelled = useRef(false);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const onRecordedRef = useRef(onRecorded);
  onRecordedRef.current = onRecorded;

  const release = useCallback(() => {
    if (timer.current) clearInterval(timer.current);
    timer.current = null;
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
    recorder.current = null;
  }, []);

  useEffect(
    () => () => {
      cancelled.current = true;
      if (recorder.current?.state === 'recording') recorder.current.stop();
      release();
    },
    [release],
  );

  const stop = useCallback(() => {
    if (recorder.current?.state === 'recording') recorder.current.stop();
  }, []);

  // תקרת אורך: עוצרים ושולחים את מה שהוקלט.
  useEffect(() => {
    if (state === 'recording' && seconds >= MAX_RECORDING_SECONDS) stop();
  }, [state, seconds, stop]);

  const start = useCallback(async () => {
    if (!isRecordingSupported()) {
      setState('unsupported');
      return;
    }
    setState('requesting');
    try {
      const media = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.current = media;
      const mimeType = pickMime();
      const rec = new MediaRecorder(media, mimeType ? { mimeType } : undefined);
      recorder.current = rec;
      chunks.current = [];
      cancelled.current = false;
      rec.ondataavailable = (e: BlobEvent) => {
        if (e.data.size > 0) chunks.current.push(e.data);
      };
      rec.onstop = () => {
        const type = rec.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(chunks.current, { type });
        chunks.current = [];
        release();
        setState('idle');
        setSeconds(0);
        if (!cancelled.current && blob.size > 0) onRecordedRef.current(blob);
      };
      rec.start();
      setSeconds(0);
      setState('recording');
      timer.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch {
      release();
      setState('denied');
    }
  }, [release]);

  const cancel = useCallback(() => {
    cancelled.current = true;
    if (recorder.current?.state === 'recording') recorder.current.stop();
    else {
      release();
      setState('idle');
    }
  }, [release]);

  return { state, seconds, start, stop, cancel };
}

export function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
